import React, { memo, useMemo, useState } from 'react';
import {
	matchedScopes,
	ScreenSpaceFlagsPlacementComponent,
} from '@post-office-placement/screen-space-flags/placement';
import { useIsEmbeddedConfluence_DO_NOT_USE } from '@confluence/embedded-confluence-check';
import Placement from '@atlassian/post-office-placement/placement';
import { ScreenSpaceFlagPlacementPreviewSwitcher } from '@atlassian/post-office-placement/preview';
import {
	PlacementFetchProvider,
	usePlacementPreloadedData,
} from '@atlassian/confluence-post-office-provider/entry-points/preload-placement';

import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { fg } from '@confluence/feature-gating';
import { expVal } from '@confluence/feature-experiments';
import { getPreloaderFnContext } from '@confluence/query-preloader-tools/entry-points/preloaderFnContext.ts';
import { ConditionalUfoHoldIgnore } from '@confluence/react-ufo-conditional-hold-ignore/entry-points/ConditionalUfoHoldIgnore.ts';
import type { RouteMatch } from '@confluence/route/entry-points/Route.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { isRestrictedFromPostOfficeSynthetic } from './is-restricted-from-post-office-synthetic.ts';
import { preloadPostOfficePlacementWithSessionContext } from './preload-placement.ts';
import { sessionToPostOfficeContext } from './session-to-post-office-context.ts';

function useIsEmbeddedView(): boolean {
	if (fg('confluence_screen_space_flags_no_embed')) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		return useIsEmbeddedConfluence_DO_NOT_USE();
	}

	return false;
}

export const ScreenSpaceFlagsPlacement = memo(() => {
	const [messageErrorToReport, setMessageErrorToReport] = useState<any | undefined>();

	const [preloadedInitialData, setPreloadedInitialData] = usePlacementPreloadedData(
		'screen-space-flags/update-messages',
	);

	const memoizedPlacementProps = useMemo(
		() => ({
			reportExpectedError: markErrorAsHandled,
			reportUnexpectedError: setMessageErrorToReport,
			preloadedInitialData,
			setPreloadedInitialData,
		}),
		[preloadedInitialData, setPreloadedInitialData],
	);

	const { userId } = useSessionData();

	const isEmbeddedView = useIsEmbeddedView();

	if (isEmbeddedView) {
		return null;
	}

	if (!userId) {
		return null;
	}

	// Suppress the placement (and its Placement API request) for non-Post-Office synthetic
	// test tenants, which otherwise generate large volumes of Placement API traffic and
	// trigger Stargate rate-limit alerts.
	if (isRestrictedFromPostOfficeSynthetic()) {
		return null;
	}

	const content = (
		<ScreenSpaceFlagPlacementPreviewSwitcher isEnabled>
			<PlacementFetchProvider>
				<Placement
					placementId="screen-space-flags"
					// @ts-ignore - Type 'ComponentType<ErrorBoundariedProps<Placement>>' is not assignable to type 'PlacementComponent<LocalPlacementProps>'
					PlacementComponent={ScreenSpaceFlagsPlacementComponent}
					placementProps={memoizedPlacementProps}
					disableInitialData
				/>

				{messageErrorToReport && <ErrorDisplay error={messageErrorToReport} />}
			</PlacementFetchProvider>
		</ScreenSpaceFlagPlacementPreviewSwitcher>
	);

	return expVal('confluence_edit_page_conditional_ufohold_ignore', 'isEnabled', false) ? (
		<ConditionalUfoHoldIgnore>{content}</ConditionalUfoHoldIgnore>
	) : (
		content
	);
});

export const preloadPostOfficeScreenSpaceFlags = async (match: RouteMatch | null) => {
	// Skip preloading (and the associated Placement API request) for non-Post-Office
	// synthetic test tenants. Without this, test tenants still hit the Placement API via the
	// SSR/preload path even though the component itself renders null.
	if (isRestrictedFromPostOfficeSynthetic()) {
		return undefined;
	}

	const sessionContext = getPreloaderFnContext();
	const activeScopes = matchedScopes({
		route: {
			hostname: '',
			pathname: match?.pathname ?? '',
			search: match?.search ?? '',
		},
		context: sessionToPostOfficeContext(sessionContext),
	});

	const updateMessagesBody = {
		current: activeScopes,
		previous: [],
	};

	return preloadPostOfficePlacementWithSessionContext('screen-space-flags', {
		resource: '/update-messages',
		requestInit: {
			method: 'POST',
			body: JSON.stringify(updateMessagesBody),
		},
	});
};
